import { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Storage as StorageIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    PlayArrow as PlayIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import {
    useGetMigrationStatusQuery,
    useApplyMigrationMutation,
    useApplyAllMigrationsMutation,
} from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';

const MigrationsPage = () => {
    const { LL } = useI18nContext();
    const {
        data: migrationData,
        isLoading,
        error,
        refetch,
    } = useGetMigrationStatusQuery(undefined);
    const [applyMigration, { isLoading: isApplying }] = useApplyMigrationMutation();
    const [applyAll, { isLoading: isApplyingAll }] = useApplyAllMigrationsMutation();

    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        migration?: string;
        all?: boolean;
    }>({ open: false });
    const [resultMessage, setResultMessage] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);

    const handleApplySingle = async (migration: string) => {
        try {
            const result = await applyMigration({ migration }).unwrap();
            if (result.success) {
                setResultMessage({
                    type: 'success',
                    message: LL.migrations.success({ migration }),
                });
                refetch();
            } else {
                setResultMessage({
                    type: 'error',
                    message: result.error || LL.migrations.failed(),
                });
            }
        } catch (err: any) {
            setResultMessage({
                type: 'error',
                message: err.data?.error || LL.migrations.failedApply(),
            });
        }
        setConfirmDialog({ open: false });
    };

    const handleApplyAll = async () => {
        try {
            const result = await applyAll().unwrap();
            if (result.success) {
                setResultMessage({
                    type: 'success',
                    message: LL.migrations.allSuccess(),
                });
                refetch();
            } else {
                let message: string = LL.migrations.someFailed();
                if (result.results) {
                    Object.entries(result.results).forEach(([migration, res]) => {
                        message += `\n${migration}: ${res.message}`;
                    });
                }
                setResultMessage({
                    type: 'error',
                    message,
                });
            }
        } catch (err: any) {
            setResultMessage({
                type: 'error',
                message: err.data?.error || LL.migrations.failedApply(),
            });
        }
        setConfirmDialog({ open: false });
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{LL.migrations.failedLoad()}</Alert>
            </Box>
        );
    }

    const status = migrationData?.data;

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <StorageIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {LL.migrations.title()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {LL.migrations.subtitle()}
                    </Typography>
                </Box>
            </Stack>

            {/* Result Message */}
            {resultMessage && (
                <Alert
                    severity={resultMessage.type}
                    onClose={() => setResultMessage(null)}
                    sx={{ mb: 3 }}
                >
                    {resultMessage.message.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                    ))}
                </Alert>
            )}

            {/* Status Cards */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <StorageIcon color="info" sx={{ fontSize: 40 }} />
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Total Migrations
                                </Typography>
                                <Typography variant="h4">{status?.total_available || 0}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Applied
                                </Typography>
                                <Typography variant="h4">{status?.total_applied || 0}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ flex: 1 }}>
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <ScheduleIcon
                                color={status?.total_pending ? 'warning' : 'disabled'}
                                sx={{ fontSize: 40 }}
                            />
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Pending
                                </Typography>
                                <Typography variant="h4">{status?.total_pending || 0}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>

            {/* Status Alert */}
            {status?.up_to_date ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
                    {LL.migrations.upToDate()}
                </Alert>
            ) : (
                <Alert severity="warning" icon={<ErrorIcon />} sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography>
                            <strong>{LL.migrations.pendingTitle()}</strong> {status?.total_pending}{' '}
                            {LL.migrations.pendingCount()}
                        </Typography>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={() => setConfirmDialog({ open: true, all: true })}
                            disabled={isApplyingAll}
                            startIcon={
                                isApplyingAll ? <CircularProgress size={16} /> : <PlayIcon />
                            }
                        >
                            {LL.migrations.applyAll()}
                        </Button>
                    </Stack>
                </Alert>
            )}

            {/* Migrations Table */}
            <Typography variant="h5" gutterBottom>
                {LL.migrations.migrations()}
            </Typography>

            {status?.available && status.available.length > 0 ? (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{LL.migrations.migration()}</TableCell>
                                <TableCell>{LL.migrations.status()}</TableCell>
                                <TableCell align="right">{LL.migrations.actions()}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.available.map((migration: string) => {
                                const isApplied = status.applied.includes(migration);
                                const isPending = status.pending.includes(migration);

                                return (
                                    <TableRow
                                        key={migration}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {migration}
                                        </TableCell>
                                        <TableCell>
                                            {isApplied ? (
                                                <Chip
                                                    label={LL.migrations.applied()}
                                                    color="success"
                                                    variant="outlined"
                                                    size="small"
                                                    icon={<CheckCircleIcon />}
                                                />
                                            ) : (
                                                <Chip
                                                    label={LL.migrations.pending()}
                                                    color="warning"
                                                    variant="outlined"
                                                    size="small"
                                                    icon={<ScheduleIcon />}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isPending && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() =>
                                                        setConfirmDialog({
                                                            open: true,
                                                            migration,
                                                        })
                                                    }
                                                    disabled={isApplying}
                                                    startIcon={
                                                        isApplying ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <PlayIcon />
                                                        )
                                                    }
                                                >
                                                    {LL.migrations.apply()}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {LL.migrations.noneFound()}
                </Alert>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })}>
                <DialogTitle>{LL.migrations.confirmTitle()}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.all
                            ? LL.migrations.confirmAll()
                            : LL.migrations.confirmOne({ migration: confirmDialog.migration })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false })}>
                        {LL.common.cancel()}
                    </Button>
                    <Button
                        onClick={() =>
                            confirmDialog.all
                                ? handleApplyAll()
                                : handleApplySingle(confirmDialog.migration!)
                        }
                        variant="contained"
                        autoFocus
                    >
                        {LL.migrations.apply()}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MigrationsPage;
